import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  ScrollArea,
  Badge,
  Group,
  Text,
  Button,
  Paper,
  LoadingOverlay,
  Title,
  Alert,
  ActionIcon,
  Modal,
  Stack,
  ThemeIcon,
  SimpleGrid,
  Box,
  Flex,
  Center,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  FileInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconPlus,
  IconTool,
  IconAlertCircle,
  IconBuildingStore,
  IconEye,
  IconFileText,
  IconDownload,
  IconCurrencyLira,
  IconCar,
  IconTrash,
  IconAlertTriangle,
  IconClockHour4,
  IconReceipt2,
  IconTrendingUp,
  IconPencil,
  IconDeviceFloppy,
  IconUpload,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import api from '../../api/axiosConfig';
import { downloadVehicleExcel } from './utils/vehicleExcelDownload';
import AddMaintenanceModal from '../../components/VehicleMaintenanceTracking/AddMaintenanceModal';

const emptyEditForm = {
  id: null,
  vehicle_id: '',
  maintenance_type: '',
  date: null,
  km_at_maintenance: '',
  next_maintenance_km: '',
  cost: '',
  provider: '',
  description: '',
  invoice_file: null,
};

export default function MaintenancePage() {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState(null);

  const [opened, { open, close }] = useDisclosure(false);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const fetchMaintenances = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/vehicles/maintenance/');
      setMaintenances(response.data || []);
    } catch (error) {
      console.error('Bakım verileri çekilemedi', error);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaintenances();
  }, []);

  const summaryStats = useMemo(() => {
    const total = maintenances.length;
    const withInvoice = maintenances.filter((m) => m.invoice_path).length;
    const totalCost = maintenances.reduce((sum, item) => sum + (item.cost || 0), 0);

    const currentMonth = dayjs().month();
    const currentYear = dayjs().year();

    const thisMonthCount = maintenances.filter((item) => {
      if (!item.date) return false;
      const d = dayjs(item.date);
      return d.month() === currentMonth && d.year() === currentYear;
    }).length;

    return { total, withInvoice, totalCost, thisMonthCount };
  }, [maintenances]);

  // Tablodaki verilerden dinamik olarak araç plaka seçeneklerini oluşturuyoruz
  const vehicleOptions = useMemo(() => {
    const map = new Map();
    maintenances.forEach((m) => {
      // m.vehicle?.id yerine doğrudan ana kayıttaki m.vehicle_id'yi kullanıyoruz
      const vId = m.vehicle_id;
      const vPlate = m.vehicle?.plate;
      
      if (vId && vPlate) {
        map.set(String(vId), { value: String(vId), label: vPlate });
      }
    });
    return Array.from(map.values());
  }, [maintenances]);

  const maintenanceTypes = [
    { value: 'Periyodik Bakım', label: 'Periyodik Bakım' },
    { value: 'Arıza Onarımı', label: 'Arıza Onarımı' },
    { value: 'Lastik Değişimi', label: 'Lastik Değişimi' },
    { value: 'Muayene', label: 'Muayene' },
    { value: 'Kaza / Hasar', label: 'Kaza / Hasar' },
    { value: 'Diğer', label: 'Diğer' },
  ];

  const handleExportCalendar = async () => {
    setExportingExcel(true);
    try {
      await downloadVehicleExcel('maintenance');
      notifications.show({ title: 'Excel indiriliyor', message: 'Bakım Planı ve Bakım Geçmişi - Tasarım V2', color: 'teal' });
    } catch (error) {
      notifications.show({ title: 'Excel indirilemedi', message: error.message, color: 'red' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleAddClick = () => open();

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    openDetail();
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    openDeleteModal();
  };

  const handleEditClick = (record) => {
    setEditForm({
      id: record.id,
      // HATA BURADAYDI: Backend'den vehicle_id direkt gelmiyorsa vehicle.id'yi almasını sağladık
      vehicle_id: String(record.vehicle_id || ''),
      maintenance_type: record.maintenance_type || '',
      date: record.date ? new Date(record.date) : null,
      km_at_maintenance: record.km_at_maintenance || '',
      next_maintenance_km: record.next_maintenance_km || '',
      cost: record.cost || '',
      provider: record.provider || '',
      description: record.description || '',
      invoice_file: null, 
    });
    openEditModal();
  };

  const handleEditSubmit = async () => {
    if (!editForm.vehicle_id || !editForm.maintenance_type || !editForm.date || !editForm.km_at_maintenance || !editForm.cost) {
      notifications.show({
        title: 'Eksik Alan',
        message: 'Araç, işlem türü, tarih, KM ve maliyet alanları zorunludur.',
        color: 'red',
      });
      return;
    }

    setUpdating(true);
    
    // Backend Form() verisi beklediği için FormData kullanıyoruz
    const formData = new FormData();
    formData.append('vehicle_id', editForm.vehicle_id);
    formData.append('maintenance_type', editForm.maintenance_type);
    formData.append('date', dayjs(editForm.date).format('YYYY-MM-DD'));
    formData.append('km_at_maintenance', editForm.km_at_maintenance);
    formData.append('cost', editForm.cost);

    if (editForm.next_maintenance_km) formData.append('next_maintenance_km', editForm.next_maintenance_km);
    if (editForm.provider) formData.append('provider', editForm.provider);
    if (editForm.description) formData.append('description', editForm.description);
    if (editForm.invoice_file) formData.append('invoice_file', editForm.invoice_file);

    try {
      const response = await api.put(`/vehicles/maintenance/${editForm.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const updatedRecord = response.data;
      setMaintenances((prev) => prev.map((m) => (m.id === updatedRecord.id ? updatedRecord : m)));

      if (selectedRecord && selectedRecord.id === updatedRecord.id) {
        setSelectedRecord(updatedRecord);
      }

      notifications.show({ title: 'Başarılı', message: 'Bakım kaydı başarıyla güncellendi.', color: 'teal' });
      closeEditModal();
    } catch (err) {
      let errorMessage = 'Kayıt güncellenemedi.';
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((e) => `${e.loc?.join('.')} alanı: ${e.msg}`).join(' | ');
      }
      notifications.show({ title: 'Hata', message: errorMessage, color: 'red' });
    } finally {
      setUpdating(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    try {
      await api.delete(`/vehicles/maintenance/${selectedRecord.id}`);
      setMaintenances(maintenances.filter((m) => m.id !== selectedRecord.id));
      closeDeleteModal();
      setSelectedRecord(null);
      notifications.show({ title: 'Başarılı', message: 'Kayıt silindi.', color: 'teal' });
    } catch (error) {
      notifications.show({ title: 'Hata', message: 'Kayıt silinirken bir hata oluştu.', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (recordId, type) => {
    try {
      const response = await api.get(`/vehicles/maintenance/${recordId}/invoice`, { responseType: 'blob' });
      const mimeType = response.data.type;
      let extension = '';

      if (mimeType === 'application/pdf') extension = '.pdf';
      else if (mimeType === 'image/jpeg') extension = '.jpg';
      else if (mimeType === 'image/png') extension = '.png';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const safeType = (type || 'Bakim').replace(/\s+/g, '_');
      link.setAttribute('download', `Fatura_${safeType}_${dayjs().format('DDMMYYYY')}${extension}`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifications.show({ title: 'Hata', message: 'Fatura bulunamadı veya indirilemedi.', color: 'red' });
    }
  };

  const getTypeColor = (type) => {
    if (!type) return 'gray';
    if (type.includes('Periyodik')) return 'blue';
    if (type.includes('Arıza')) return 'red';
    if (type.includes('Lastik')) return 'orange';
    return 'gray';
  };

  const formatCurrency = (value) =>
    (value || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });

  const rows = maintenances.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        {item.vehicle?.plate ? (
          <Badge size="lg" variant="light" color="dark" radius="md" styles={{ label: { fontWeight: 800 } }}>
            {item.vehicle.plate}
          </Badge>
        ) : (
          <Badge color="red" radius="xl">Araç Silinmiş</Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={getTypeColor(item.maintenance_type)} variant="light" radius="xl">
          {item.maintenance_type || '-'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{item.date ? dayjs(item.date).format('DD.MM.YYYY') : '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <ThemeIcon variant="light" color="gray" radius="xl" size="sm">
            <IconBuildingStore size={12} />
          </ThemeIcon>
          <Text size="sm" fw={600} c={item.provider ? 'dark' : 'dimmed'}>
            {item.provider || '-'}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600}>
          {item.km_at_maintenance ? `${item.km_at_maintenance.toLocaleString()} km` : '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600} c={item.next_maintenance_km ? 'dark' : 'dimmed'}>
          {item.next_maintenance_km ? `${item.next_maintenance_km.toLocaleString()} km` : '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={800} c="red.7">{formatCurrency(item.cost)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1} title={item.description} style={{ maxWidth: '220px' }}>
          {item.description || '-'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={6} justify="flex-end" wrap="nowrap">
          <ActionIcon
            variant="light"
            color={item.invoice_path ? 'violet' : 'blue'}
            radius="xl"
            size="lg"
            onClick={() => handleViewDetails(item)}
            title="Detay / Fatura Görüntüle"
          >
            {item.invoice_path ? <IconFileText size={18} /> : <IconEye size={18} />}
          </ActionIcon>
          <ActionIcon
            variant="light"
            color="orange"
            radius="xl"
            size="lg"
            onClick={() => handleEditClick(item)}
            title="Düzenle"
          >
            <IconPencil size={18} />
          </ActionIcon>
          <ActionIcon variant="light" color="red" radius="xl" size="lg" onClick={() => handleDeleteClick(item)} title="Sil">
            <IconTrash size={18} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg" pos="relative" style={{ minHeight: '70vh' }}>
      <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />

      {/* HERO & ÖZET KARTLAR (Değişiklik yapılmadı) */}
      <Paper radius="xl" p={{ base: 'lg', md: 'xl' }} style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 40%, rgba(37,99,235,0.88) 100%)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 18px 45px rgba(15,23,42,0.18)' }}>
        <Box style={{ position: 'absolute', top: -70, right: -70, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <Box style={{ position: 'absolute', bottom: -60, left: -60, width: 180, height: 180, borderRadius: '50%', background: 'rgba(96,165,250,0.18)' }} />
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} wrap="wrap" gap="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon size={54} radius="xl" variant="black" color="rgba(255,255,255,0.12)">
              <IconTool size={28} color="white" />
            </ThemeIcon>
            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.75)">Araç Filosu Yönetimi</Text>
              <Title order={2} c="white" fw={900} style={{ letterSpacing: '-0.7px' }}>Bakım Takvimi ve Geçmişi</Title>
              <Text size="sm" maw={720} style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
                Tüm bakım, servis ve onarım kayıtlarını tek ekrandan takip edin, maliyetleri görüntüleyin ve belge süreçlerini yönetin.
              </Text>
            </div>
          </Group>
          <Paper radius="xl" p="md" style={{ minWidth: 240, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}>
            <Group wrap="nowrap">
              <ThemeIcon size={42} radius="xl" variant="black" color="rgba(255,255,255,0.14)">
                <IconClockHour4 size={20} color="white" />
              </ThemeIcon>
              <div>
                <Text size="xs" fw={700} c="rgba(255,255,255,0.70)">Güncel Durum</Text>
                <Text fw={800} c="white">{maintenances.length} kayıt görüntüleniyor</Text>
              </div>
            </Group>
          </Paper>
        </Flex>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        <Paper radius="xl" p="lg" withBorder style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 8px 24px rgba(15,23,42,0.05)' }}>
          <Group justify="space-between">
            <div><Text size="sm" c="dimmed" fw={600}>Toplam Kayıt</Text><Title order={2} fw={900}>{summaryStats.total}</Title></div>
            <ThemeIcon size={52} radius="xl" variant="light" color="blue"><IconTool size={26} /></ThemeIcon>
          </Group>
        </Paper>
        <Paper radius="xl" p="lg" withBorder style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 8px 24px rgba(15,23,42,0.05)' }}>
          <Group justify="space-between">
            <div><Text size="sm" c="dimmed" fw={600}>Bu Ay İşlem</Text><Title order={2} fw={900}>{summaryStats.thisMonthCount}</Title></div>
            <ThemeIcon size={52} radius="xl" variant="light" color="teal"><IconTrendingUp size={26} /></ThemeIcon>
          </Group>
        </Paper>
        <Paper radius="xl" p="lg" withBorder style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 8px 24px rgba(15,23,42,0.05)' }}>
          <Group justify="space-between">
            <div><Text size="sm" c="dimmed" fw={600}>Faturalı Kayıt</Text><Title order={2} fw={900}>{summaryStats.withInvoice}</Title></div>
            <ThemeIcon size={52} radius="xl" variant="light" color="violet"><IconReceipt2 size={26} /></ThemeIcon>
          </Group>
        </Paper>
        <Paper radius="xl" p="lg" withBorder style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 8px 24px rgba(15,23,42,0.05)' }}>
          <Group justify="space-between">
            <div><Text size="sm" c="dimmed" fw={600}>Toplam Maliyet</Text><Title order={3} fw={900}>{formatCurrency(summaryStats.totalCost)}</Title></div>
            <ThemeIcon size={52} radius="xl" variant="light" color="red"><IconCurrencyLira size={26} /></ThemeIcon>
          </Group>
        </Paper>
      </SimpleGrid>

      {error && <Alert icon={<IconAlertCircle size={18} />} title="Hata" color="red" variant="light" radius="xl">{error}</Alert>}

      <Paper radius="xl" withBorder p="md" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap="md">
          <div>
            <Title order={4} fw={800}>Bakım Kayıtları</Title>
            <Text size="sm" c="dimmed">Tüm servis ve bakım hareketleri listelenmektedir</Text>
          </div>
          <Group gap="sm" wrap="wrap">
            <Button
              color="teal"
              variant="light"
              radius="xl"
              leftSection={<IconDownload size={16} />}
              loading={exportingExcel}
              onClick={handleExportCalendar}
            >
              Bakım Takvimi Excel İndir (V2)
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAddClick} radius="xl">Yeni Bakım Girişi</Button>
          </Group>
        </Flex>
      </Paper>

      {/* MEVCUT MODALLAR */}
      <AddMaintenanceModal opened={opened} close={close} onSuccess={fetchMaintenances} />

      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} centered zIndex={300} radius="xl" overlayProps={{ blur: 3 }} title={<Group gap="sm"><ThemeIcon color="red" variant="light" radius="xl" size="md"><IconAlertTriangle size={18} /></ThemeIcon><Text fw={800} size="lg">Silme Onayı</Text></Group>}>
        <Stack gap="lg">
          <Paper p="md" radius="lg" style={{ background: 'var(--mantine-color-red-0)', border: '1px solid var(--mantine-color-red-2)' }}>
            <Text size="sm">Bu bakım kaydını silmek istediğinize emin misiniz?</Text>
            <Text size="sm" c="red.7" fw={600} mt="sm">Eğer bu kayda ait bir fatura veya belge varsa o da sistemden kaldırılacaktır. Bu işlem geri alınamaz.</Text>
          </Paper>
          <Group justify="flex-end">
            <Button variant="default" radius="xl" onClick={closeDeleteModal}>Vazgeç</Button>
            <Button color="red" radius="xl" onClick={confirmDelete} leftSection={<IconTrash size={16} />}>Evet, Sil</Button>
          </Group>
        </Stack>
      </Modal>

{/* DETAY MODALI */}
      <Modal opened={detailOpened} onClose={closeDetail} centered size="md" radius="xl" overlayProps={{ blur: 3 }} title={<Text fw={800} size="lg">Bakım İşlem Detayı</Text>}>
        {selectedRecord && (
          <Stack gap="md">
            <Paper radius="xl" p="md" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.94) 45%, rgba(37,99,235,0.86) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Group justify="space-between" align="center">
                <Group>
                  <ThemeIcon size="xl" radius="xl" variant="white" color="rgba(255,255,255,0.12)"><IconCar size={22} color="white" /></ThemeIcon>
                  <div>
                    <Text fw={800} size="lg" c="white">{selectedRecord.vehicle?.plate || '-'}</Text>
                    <Text size="xs" c="rgba(255,255,255,0.72)">{selectedRecord.vehicle?.brand || 'Araç bilgisi yok'}</Text>
                  </div>
                </Group>
                <Badge size="lg" radius="xl" color={getTypeColor(selectedRecord.maintenance_type)} variant="light">{selectedRecord.maintenance_type || '-'}</Badge>
              </Group>
            </Paper>
            <SimpleGrid cols={2} spacing="md">
              <Paper withBorder p="md" radius="xl">
                <Text size="xs" c="dimmed" mb={4}>İşlem Tarihi</Text>
                <Text fw={700}>{selectedRecord.date ? dayjs(selectedRecord.date).format('DD MMMM YYYY') : '-'}</Text>
              </Paper>
              <Paper withBorder p="md" radius="xl">
                <Text size="xs" c="dimmed" mb={4}>Servis / Firma</Text>
                <Text fw={700}>{selectedRecord.provider || 'Belirtilmemiş'}</Text>
              </Paper>
              <Paper withBorder p="md" radius="xl">
                <Text size="xs" c="dimmed" mb={4}>Yapılan KM</Text>
                <Text fw={700}>{selectedRecord.km_at_maintenance ? `${selectedRecord.km_at_maintenance.toLocaleString()} km` : '-'}</Text>
              </Paper>
              <Paper withBorder p="md" radius="xl">
                <Text size="xs" c="dimmed" mb={4}>Sonraki Bakım KM</Text>
                <Text fw={700}>{selectedRecord.next_maintenance_km ? `${selectedRecord.next_maintenance_km.toLocaleString()} km` : '-'}</Text>
              </Paper>
            </SimpleGrid>
            <Paper withBorder p="md" radius="xl" style={{ background: 'var(--mantine-color-red-0)', borderColor: 'var(--mantine-color-red-2)' }}>
              <Text size="xs" fw={700} c="red.7" mb={4}>Toplam Maliyet</Text>
              <Text fw={900} size="xl" c="red.7">{formatCurrency(selectedRecord.cost)}</Text>
            </Paper>
            <Paper withBorder p="md" radius="xl">
              <Text size="xs" fw={700} c="dimmed" mb="xs">Açıklama / Yapılan İşlemler</Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedRecord.description || 'Açıklama girilmemiş.'}</Text>
            </Paper>

            {/* YANLIŞLIKLA SİLDİĞİM FATURA İNDİRME KISMI BURASI :) */}
            <Paper withBorder p="md" radius="xl" bg={selectedRecord.invoice_path ? 'violet.0' : 'gray.0'} style={{ borderColor: selectedRecord.invoice_path ? 'var(--mantine-color-violet-2)' : 'var(--mantine-color-gray-2)' }}>
              <Group justify="space-between" align="center">
                <Group gap="sm">
                  <ThemeIcon size="lg" radius="xl" color={selectedRecord.invoice_path ? 'violet' : 'gray'} variant="light">
                    <IconFileText size={20} />
                  </ThemeIcon>
                  <div>
                    <Text fw={700} size="sm" c={selectedRecord.invoice_path ? 'violet.9' : 'dimmed'}>
                      {selectedRecord.invoice_path ? 'Fatura Eki Mevcut' : 'Fatura Eklenmemiş'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {selectedRecord.invoice_path ? 'İşleme ait belgeyi indirebilirsiniz.' : 'Bu işleme ait bir belge bulunmuyor.'}
                    </Text>
                  </div>
                </Group>
                {selectedRecord.invoice_path && (
                  <Button variant="light" color="violet" radius="xl" leftSection={<IconDownload size={16} />} onClick={() => handleDownloadInvoice(selectedRecord.id, selectedRecord.maintenance_type)}>
                    İndir
                  </Button>
                )}
              </Group>
            </Paper>
            {/* FATURA İNDİRME KISMI BİTİŞ */}

            <Group justify="space-between" mt="sm">
              <Button variant="light" color="orange" radius="xl" leftSection={<IconPencil size={16} />} onClick={() => { closeDetail(); handleEditClick(selectedRecord); }}>Düzenle</Button>
              <Button variant="default" radius="xl" onClick={closeDetail}>Kapat</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* YENİ EKLENEN: DÜZENLEME MODALI */}
      <Modal opened={editModalOpened} onClose={closeEditModal} title={<Text fw={800} size="lg">Bakım Kaydını Düzenle</Text>} radius="xl" centered size="lg" overlayProps={{ blur: 3 }}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label="Araç Plakası"
              placeholder="Araç Seçin"
              data={vehicleOptions}
              value={editForm.vehicle_id}
              onChange={(val) => setEditForm((p) => ({ ...p, vehicle_id: val || '' }))}
              searchable
              required
              radius="md"
            />
            <Select
              label="İşlem Türü"
              placeholder="Tür Seçin"
              data={maintenanceTypes}
              value={editForm.maintenance_type}
              onChange={(val) => setEditForm((p) => ({ ...p, maintenance_type: val || '' }))}
              required
              radius="md"
            />
            <DatePickerInput
              label="Tarih"
              placeholder="Tarih Seçin"
              value={editForm.date}
              onChange={(val) => setEditForm((p) => ({ ...p, date: val }))}
              valueFormat="DD.MM.YYYY"
              required
              radius="md"
            />
            <TextInput
              label="Servis / Firma Adı"
              placeholder="Örn: Bosch Car Service"
              value={editForm.provider}
              onChange={(e) => {
                const val = e.currentTarget.value;
                setEditForm((p) => ({ ...p, provider: val }));
              }}
              radius="md"
            />
            <NumberInput
              label="İşlem Yapılan KM"
              placeholder="Örn: 125000"
              value={editForm.km_at_maintenance === '' ? '' : Number(editForm.km_at_maintenance)}
              onChange={(val) => setEditForm((p) => ({ ...p, km_at_maintenance: val === undefined ? '' : String(val) }))}
              required
              hideControls
              radius="md"
            />
            <NumberInput
              label="Sonraki Bakım KM"
              placeholder="Örn: 135000"
              value={editForm.next_maintenance_km === '' ? '' : Number(editForm.next_maintenance_km)}
              onChange={(val) => setEditForm((p) => ({ ...p, next_maintenance_km: val === undefined ? '' : String(val) }))}
              hideControls
              radius="md"
            />
          </SimpleGrid>

          <Paper withBorder radius="md" p="sm" bg="red.0" style={{ borderColor: 'var(--mantine-color-red-2)' }}>
            <NumberInput
              label="Toplam Maliyet (TL)"
              placeholder="0.00"
              value={editForm.cost === '' ? '' : Number(editForm.cost)}
              onChange={(val) => setEditForm((p) => ({ ...p, cost: val === undefined ? '' : String(val) }))}
              required
              hideControls
              decimalScale={2}
              radius="md"
              styles={{ input: { fontWeight: 700, color: 'var(--mantine-color-red-7)' } }}
            />
          </Paper>

          <Textarea
            label="Açıklama / Yapılan İşlemler"
            placeholder="Değişen parçalar, onarım detayları..."
            value={editForm.description}
            onChange={(e) => {
              const val = e.currentTarget.value;
              setEditForm((p) => ({ ...p, description: val }));
            }}
            minRows={3}
            autosize
            radius="md"
          />

          <FileInput
            label="Yeni Fatura / Belge Yükle"
            description="Mevcut belgeyi değiştirmek istiyorsanız yeni bir dosya seçin."
            placeholder="Dosya seçmek için tıklayın"
            accept="image/png,image/jpeg,application/pdf"
            value={editForm.invoice_file}
            onChange={(file) => setEditForm((p) => ({ ...p, invoice_file: file }))}
            leftSection={<IconUpload size={16} />}
            clearable
            radius="md"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" radius="xl" onClick={closeEditModal}>İptal</Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} radius="xl" loading={updating} onClick={handleEditSubmit}>
              Değişiklikleri Kaydet
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* TABLO */}
      <Paper radius="xl" withBorder style={{ overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
        <ScrollArea h="65vh" type="auto" offsetScrollbars>
          <Table stickyHeader stickyHeaderOffset={0} striped highlightOnHover verticalSpacing="md" horizontalSpacing="md" style={{ minWidth: 1100 }}>
            <Table.Thead style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
              <Table.Tr>
                <Table.Th>Plaka</Table.Th>
                <Table.Th>İşlem Türü</Table.Th>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Firma / Servis</Table.Th>
                <Table.Th>Yapılan KM</Table.Th>
                <Table.Th>Sonraki Bakım KM</Table.Th>
                <Table.Th>Maliyet</Table.Th>
                <Table.Th>Açıklama</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows}
              {!loading && maintenances.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={9}>
                    <Center py={70}>
                      <Stack align="center" gap="sm">
                        <ThemeIcon size={72} radius="xl" variant="light" color="gray"><IconTool size={34} /></ThemeIcon>
                        <Title order={4}>Bakım kaydı bulunamadı</Title>
                        <Text c="dimmed" size="sm" ta="center" maw={420}>Henüz kayıtlı bakım işlemi bulunmuyor. Yeni bakım girişi ekleyerek listeyi oluşturmaya başlayabilirsiniz.</Text>
                      </Stack>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>
    </Stack>
  );
}